import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Cookie, 
  Settings, 
  X, 
  Shield, 
  BarChart3, 
  Target, 
  Eye,
  Check,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const CookieConsentManager = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  const [cookieSettings, setCookieSettings] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    functional: false
  });

  // Cookie categories configuration
  const cookieCategories = [
    {
      id: 'essential',
      name: 'Notwendige Cookies',
      description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.',
      required: true,
      icon: Shield,
      color: 'text-green-400',
      bgColor: 'bg-green-500/15',
      details: [
        'Session-Management',
        'Sicherheitsfeatures',
        'Grundlegende Website-Funktionen',
        'CSRF-Schutz'
      ]
    },
    {
      id: 'analytics',
      name: 'Analyse Cookies',
      description: 'Helfen uns zu verstehen, wie Besucher unsere Website nutzen.',
      required: false,
      icon: BarChart3,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/15',
      details: [
        'Google Analytics',
        'Seitenaufrufe und Verweildauer',
        'Nutzerverhalten-Analyse',
        'Performance-Optimierung'
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Werden verwendet, um Ihnen relevante Werbung zu zeigen.',
      required: false,
      icon: Target,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/15',
      details: [
        'Personalisierte Werbung',
        'Social Media Integration',
        'Retargeting',
        'Conversion-Tracking'
      ]
    },
    {
      id: 'functional',
      name: 'Funktionale Cookies',
      description: 'Ermöglichen erweiterte Funktionen und Personalisierung.',
      required: false,
      icon: Settings,
      color: 'text-brand-400',
      bgColor: 'bg-brand/15',
      details: [
        'Spracheinstellungen',
        'Benutzervorlieben',
        'Chat-Funktionen',
        'Interaktive Features'
      ]
    }
  ];

  // Check if consent has been given
  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a small delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      const savedSettings = JSON.parse(consent);
      setCookieSettings(savedSettings);
      // Apply cookie settings (Google Analytics, etc.)
      applyCookieSettings(savedSettings);
    }
  }, []);

  const applyCookieSettings = (settings) => {
    // Google Analytics: Einwilligung nachziehen. Plausible braucht keine,
    // es kommt ohne Cookies aus.
    if (settings.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }

    // Apply other cookie settings as needed
    if (settings.marketing) {
      // Enable marketing cookies
    }

    if (settings.functional) {
      // Enable functional cookies  
    }
  };

  const handleAcceptAll = () => {
    const newSettings = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    setCookieSettings(newSettings);
    localStorage.setItem('cookie-consent', JSON.stringify(newSettings));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    applyCookieSettings(newSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleDeclineAll = () => {
    const newSettings = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    setCookieSettings(newSettings);
    localStorage.setItem('cookie-consent', JSON.stringify(newSettings));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    applyCookieSettings(newSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(cookieSettings));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    applyCookieSettings(cookieSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleCategoryToggle = (categoryId) => {
    if (categoryId === 'essential') return; // Can't disable essential cookies
    
    setCookieSettings(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      {!showSettings && (
        <div className="theme-brand fixed bottom-0 left-0 right-0 z-50 bg-surface text-ink border-t border-hairline p-4 shadow-2xl">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <Cookie className="h-8 w-8 text-brand-400" />
                <div>
                  <h3 className="text-lg font-semibold">Cookie-Einstellungen</h3>
                </div>
              </div>
              
              <div className="flex-1">
                <p className="text-ink-muted text-sm leading-relaxed">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Sie können Ihre Einstellungen jederzeit anpassen. Weitere Informationen finden Sie in unserer{' '}
                  <a href="/privacy" className="text-brand-400 hover:text-brand-300 underline">
                    Datenschutzerklärung
                  </a>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setShowSettings(true)}
                  className="border-hairline-strong text-ink-muted hover:bg-surface-raised hover:text-white"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Einstellungen
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeclineAll}
                  className="border-hairline-strong text-ink-muted hover:bg-surface-raised hover:text-white"
                >
                  Ablehnen
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-brand hover:bg-brand-600 text-ink-onBrand"
                >
                  <Check className="mr-2 h-4 w-4" />
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="theme-brand fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden border-hairline-strong">
            <div className="bg-gradient-to-r from-slate-900 to-teal-900 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Cookie className="h-8 w-8 text-brand-400" />
                  <div>
                    <h2 className="text-2xl font-bold">Cookie-Einstellungen</h2>
                    <p className="text-ink-muted">Verwalten Sie Ihre Cookie-Präferenzen</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowSettings(false)}
                  className="text-ink-muted hover:text-white hover:bg-white/[0.07]"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            <CardContent className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6">
                <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-400/25">
                  <div className="flex items-start gap-3">
                    <Eye className="h-5 w-5 text-blue-400 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-200 mb-1">
                        Über Cookies
                      </h3>
                      <p className="text-blue-300 text-sm">
                        Cookies sind kleine Textdateien, die von Websites verwendet werden, um die Benutzererfahrung 
                        zu verbessern. Wir verwenden verschiedene Arten von Cookies für verschiedene Zwecke.
                      </p>
                    </div>
                  </div>
                </div>

                {cookieCategories.map((category) => {
                  const Icon = category.icon;
                  const isExpanded = expandedCategory === category.id;
                  
                  return (
                    <div key={category.id} className="border border-hairline rounded-lg">
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`p-2 rounded-lg ${category.bgColor}`}>
                              <Icon className={`h-5 w-5 ${category.color}`} />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-ink">
                                  {category.name}
                                </h3>
                                {category.required && (
                                  <span className="bg-green-500/15 text-green-300 text-xs px-2 py-1 rounded-full">
                                    Erforderlich
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-ink-muted">
                                {category.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Switch
                              checked={cookieSettings[category.id]}
                              onCheckedChange={() => handleCategoryToggle(category.id)}
                              disabled={category.required}
                              className="data-[state=checked]:bg-brand"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleCategoryExpansion(category.id)}
                              className="text-ink-faint hover:text-ink-muted p-1"
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-hairline">
                            <h4 className="font-medium text-ink mb-2">
                              Was wird erfasst:
                            </h4>
                            <ul className="space-y-1">
                              {category.details.map((detail, index) => (
                                <li key={index} className="flex items-center gap-2 text-sm text-ink-muted">
                                  <div className="w-1 h-1 bg-ink-faint rounded-full"></div>
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>

            <div className="bg-surface-sunken p-6 border-t">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={handleDeclineAll}
                  className="border-hairline-strong text-ink-muted hover:bg-surface-raised"
                >
                  Alle ablehnen
                </Button>
                <Button
                  variant="outline"
                  onClick={handleAcceptAll}
                  className="border-hairline-strong text-ink-muted hover:bg-surface-raised"
                >
                  Alle akzeptieren
                </Button>
                <Button
                  onClick={handleSaveSettings}
                  className="bg-brand hover:bg-brand-600 text-ink-onBrand"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Einstellungen speichern
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export default CookieConsentManager;